import { Component, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzFormItemComponent, NzFormModule } from 'ng-zorro-antd/form';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzUploadFile, NzUploadModule } from 'ng-zorro-antd/upload';
import { BehaviorSubject, finalize, Observable, tap } from 'rxjs';
import { CommonModule } from '@angular/common';
import { OrganizationService } from '../../organization.service';
import { DrawerService } from '../../../../core/services/drawer.service';
import { DRAWER_IDS } from '../../../../core/constants/drawer-ids.constant';
import { NzNotificationModule } from 'ng-zorro-antd/notification';
import { ErrorResponse } from '../../../../core/models/error.models';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzIconModule, NzIconService } from 'ng-zorro-antd/icon';
import { FileExcelOutline } from '@ant-design/icons-angular/icons';
import { CommonService } from '../../../../shared/services/common.service';
import { LoaderService } from '../../../../core/services/loader.service';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { UploadProgressComponent } from '../../../../components/upload-progress/upload-progress.component';
import { UploadEvent } from '../../../../core/services/upload-progress.service';
import { SearchSelectComponent } from '../../../../components/search-select/search-select.component';

@Component({
  selector: 'app-import-organizations',
  standalone: true,
  imports: [
    NzFormItemComponent,
    FormsModule,
    NzDropDownModule,
    NzSelectModule,
    NzUploadModule,
    SvgIconComponent,
    NzButtonModule,
    CommonModule,
    ReactiveFormsModule,
    NzNotificationModule,
    NzAlertModule,
    NzFormModule,
    NzSpinModule,
    NzIconModule,
    NzProgressModule,
    UploadProgressComponent,
    SearchSelectComponent,
  ],
  templateUrl: './import-organizations.component.html',
  styleUrl: './import-organizations.component.scss',
})
export class ImportOrganizationsComponent {
  organizationForm: FormGroup;
  fileList: NzUploadFile[] = [];
  private readonly nzIconService = inject(NzIconService);
  private readonly progressSubject = new BehaviorSubject<number>(0);
  uploadProgress$ = this.progressSubject.asObservable();

  loading$: Observable<boolean>;

  constructor(
    private readonly fb: FormBuilder,
    private readonly organizationService: OrganizationService,
    private readonly drawerService: DrawerService,
    private readonly commonService: CommonService,
    private readonly loaderService: LoaderService
  ) {
    this.organizationForm = this.fb.group({
      orgparentorg: [null],
      file: [null, [Validators.required]],
    });
    this.nzIconService.addIcon(FileExcelOutline);
    this.loading$ = this.loaderService.loading$;
  }

  beforeUpload = (file: NzUploadFile): boolean /* NOSONAR */ => {
    const isValidType =
      file.type === 'application/vnd.ms-excel' ||
      file.type === 'text/xlsx' ||
      file.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

    if (!isValidType) {
      this.commonService.notify(
        'error',
        'You can only upload XLS, XLSX files.'
      );
      this.fileList = [];
      return false;
    }

    this.fileList = [file];
    return false;
  };

  handleOrganizationFileRemove = (file: NzUploadFile): boolean => {
    this.fileList = this.fileList.filter(item => item !== file);
    return true;
  };

  customUpload = (): void => {
    const parentOrgId = this.organizationForm.get('orgparentorg')?.value ?? 0;

    if (!this.fileList.length) {
      this.commonService.notify('error', 'Please select a file first');
      return;
    }

    const uploadFile = this.fileList[0] as unknown as File;
    this.loaderService.show();
    this.progressSubject.next(0);

    this.organizationService
      .bulkCreateOrganizations(uploadFile, parentOrgId)
      .pipe(
        tap((event: UploadEvent<void>) => {
          if (event.status === 'progress') {
            this.progressSubject.next(event.percentage);
          } else if (event.status === 'completed') {
            this.progressSubject.next(100);
          }
        }),
        finalize(() => {
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: (event: UploadEvent<void>) => {
          if (event.status === 'completed') {
            this.fileList = [];
            this.commonService.notify(
              'success',
              'The file has been uploaded successfully, and we will inform you by email once it has been processed.'
            );
            this.closeImportDrawer();
          }
        },
        error: ({ errors, message }: ErrorResponse) => {
          if (message === 'Request Timeout') {
            this.progressSubject.next(80);
          } else {
            this.progressSubject.next(0);
          }

          const errorMessages = errors.map(
            err => err.detailedMessage || err.description
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  };

  closeImportDrawer(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.IMPORT);
  }
}
